import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  InputNumber,
  Switch,
  Slider,
  Typography,
  Spin,
  message,
  Space,
  Divider,
  Select,
  Row,
  Col,
  Tag,
  Alert,
} from 'antd';
import {
  ThunderboltOutlined,
  MinusOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  StockOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const QuickTrade = ({ match, history }) => {
  const tradeId = match?.params?.tradeId;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tradeInfo, setTradeInfo] = useState(null);
  const [error, setError] = useState(null);

  // 주문 입력 상태
  const [orderType, setOrderType] = useState('buy');
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);

  // 현재가 별도 상태 (수동 refresh 지원)
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceChange, setPriceChange] = useState(null);
  const [priceChangePercent, setPriceChangePercent] = useState(null);

  // 가격 추종 설정
  const [priceFollow, setPriceFollow] = useState(false);
  const [maxFollowRange, setMaxFollowRange] = useState(2);

  // 만료 카운트다운
  const [ttlSeconds, setTtlSeconds] = useState(0);

  // 종목 정보 로드
  const fetchTradeInfo = useCallback(async () => {
    if (!tradeId) {
      setError('잘못된 접근입니다.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get(`/api/data_predator/quick-trade/${tradeId}/`);
      const data = res.data;
      if (data.success) {
        setTradeInfo(data);
        if (data.current_price) {
          setCurrentPrice(data.current_price);
          setPrice(data.current_price);
        }
        setTtlSeconds(data.ttl_seconds || 600);
      } else {
        setError(data.error || '종목 정보를 불러올 수 없습니다.');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('퀵 트레이드 링크가 만료되었습니다. (유효시간: 10분)');
      } else {
        setError('종목 정보를 불러오는 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  }, [tradeId]);

  // 현재가 수동 refresh
  const fetchPrice = useCallback(async () => {
    if (!tradeId || priceLoading) return;
    try {
      setPriceLoading(true);
      const res = await api.get(`/api/data_predator/quick-trade/${tradeId}/price/`);
      const data = res.data;
      if (data.success && data.current_price) {
        setCurrentPrice(data.current_price);
        setPriceChange(data.change);
        setPriceChangePercent(data.change_percent);
        // 주문가도 현재가로 동기화
        setPrice(data.current_price);
        message.success('현재가가 갱신되었습니다.');
      } else {
        message.warning('현재가를 가져올 수 없습니다. 잠시 후 다시 시도해주세요.');
      }
    } catch (err) {
      message.error('현재가 조회 중 오류가 발생했습니다.');
    } finally {
      setPriceLoading(false);
    }
  }, [tradeId, priceLoading]);

  // 계좌 목록 로드
  const fetchAccounts = useCallback(async () => {
    try {
      const res = await api.get('/api/data_predator/accounts/');
      const allAccounts = res.data?.results || res.data || [];
      // 키움 계좌만 필터링
      const kiwoomAccounts = allAccounts.filter(
        (a) => a.broker === 'kiwoom' && a.active !== false
      );
      setAccounts(kiwoomAccounts);
      if (kiwoomAccounts.length > 0) {
        setSelectedAccount(kiwoomAccounts[0].account_number);
      }
    } catch (err) {
      console.warn('계좌 목록 조회 실패:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchTradeInfo();
    fetchAccounts();
  }, [fetchTradeInfo, fetchAccounts]);

  // TTL 카운트다운
  useEffect(() => {
    if (ttlSeconds <= 0) return;
    const timer = setInterval(() => {
      setTtlSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setError('퀵 트레이드 링크가 만료되었습니다.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [ttlSeconds]);

  const formatTtl = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const totalAmount = quantity * price;

  const handleConfirm = async () => {
    if (!selectedAccount) {
      message.error('계좌를 선택해주세요.');
      return;
    }
    if (quantity <= 0) {
      message.error('수량을 1 이상 입력해주세요.');
      return;
    }
    if (price <= 0) {
      message.error('가격을 입력해주세요.');
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.post('/api/data_predator/quick-trade/confirm/', {
        trade_id: tradeId,
        order_type: orderType,
        quantity,
        price,
        account_number: selectedAccount,
        broker: tradeInfo?.broker || 'kiwoom',
      });

      if (res.data.success) {
        message.success(res.data.message || '주문이 접수되었습니다.');
        // 주문 완료 후 트랜잭션 페이지로 이동
        setTimeout(() => {
          history.push('/transactions');
        }, 1500);
      } else {
        message.error(res.data.error || '주문 실패');
      }
    } catch (err) {
      const errMsg =
        err.response?.data?.error || '주문 실행 중 오류가 발생했습니다.';
      message.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  // 로딩 중
  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f0f2f5',
        }}
      >
        <Spin size="large" tip="종목 정보 로딩 중..." />
      </div>
    );
  }

  // 오류 상태
  if (error) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f0f2f5',
          padding: '24px',
        }}
      >
        <Card style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <Space direction="vertical" size="large">
            <ThunderboltOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
            <Title level={4} style={{ color: '#ff4d4f', margin: 0 }}>
              퀵 트레이드 오류
            </Title>
            <Text type="secondary">{error}</Text>
            <Button type="primary" onClick={() => history.push('/dashboard')}>
              대시보드로 이동
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f0f2f5',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div style={{ maxWidth: 480, width: '100%' }}>
        {/* 헤더 */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Space align="center">
            <ThunderboltOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
              퀵 트레이드
            </Title>
          </Space>
          <div style={{ marginTop: 4 }}>
            <Tag
              icon={<ClockCircleOutlined />}
              color={ttlSeconds > 60 ? 'blue' : 'red'}
            >
              만료까지 {formatTtl(ttlSeconds)}
            </Tag>
          </div>
        </div>

        {/* 조건식 정보 */}
        {tradeInfo?.condition_name && (
          <Alert
            message={`조건식: ${tradeInfo.condition_name}`}
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}

        {/* 종목 정보 카드 */}
        <Card
          style={{ marginBottom: 12, borderRadius: 8 }}
          bodyStyle={{ padding: '16px' }}
        >
          <Row align="middle" justify="space-between" style={{ marginBottom: 12 }}>
            <Col>
              <Space>
                <StockOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                <div>
                  <div>
                    <Text strong style={{ fontSize: 18 }}>
                      {tradeInfo?.stock_name || tradeInfo?.symbol}
                    </Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {tradeInfo?.symbol} · {tradeInfo?.broker?.toUpperCase()}
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Space direction="vertical" align="end" size={2}>
                <Space align="center">
                  {currentPrice ? (
                    <Text strong style={{ fontSize: 20, color: '#f5222d' }}>
                      {currentPrice.toLocaleString()}원
                    </Text>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      가격 조회 중...
                    </Text>
                  )}
                  <Button
                    icon={<ReloadOutlined />}
                    size="small"
                    type="text"
                    loading={priceLoading}
                    onClick={fetchPrice}
                    title="현재가 새로고침"
                    style={{ color: '#1890ff' }}
                  />
                </Space>
                {priceChange !== null && priceChangePercent !== null && (
                  <Text
                    style={{
                      fontSize: 12,
                      color: priceChange >= 0 ? '#f5222d' : '#1890ff',
                    }}
                  >
                    {priceChange >= 0 ? '+' : ''}
                    {priceChange?.toLocaleString()}원 (
                    {priceChange >= 0 ? '+' : ''}
                    {priceChangePercent?.toFixed(2)}%)
                  </Text>
                )}
              </Space>
            </Col>
          </Row>

          <Divider style={{ margin: '12px 0' }} />

          {/* 매수/매도 선택 */}
          <Row gutter={8} style={{ marginBottom: 12 }}>
            <Col span={12}>
              <Button
                block
                type={orderType === 'buy' ? 'primary' : 'default'}
                onClick={() => setOrderType('buy')}
                style={{
                  background: orderType === 'buy' ? '#f5222d' : undefined,
                  borderColor: orderType === 'buy' ? '#f5222d' : undefined,
                }}
              >
                매수
              </Button>
            </Col>
            <Col span={12}>
              <Button
                block
                type={orderType === 'sell' ? 'primary' : 'default'}
                onClick={() => setOrderType('sell')}
                style={{
                  background: orderType === 'sell' ? '#1890ff' : undefined,
                  borderColor: orderType === 'sell' ? '#1890ff' : undefined,
                }}
              >
                매도
              </Button>
            </Col>
          </Row>

          {/* 수량 조절 */}
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              주문 수량
            </Text>
            <Row align="middle" gutter={8}>
              <Col>
                <Button
                  icon={<MinusOutlined />}
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                />
              </Col>
              <Col flex="auto">
                <InputNumber
                  value={quantity}
                  onChange={(val) => setQuantity(Math.max(1, val || 1))}
                  min={1}
                  style={{ width: '100%', textAlign: 'center' }}
                  formatter={(v) => `${v} 주`}
                  parser={(v) => parseInt(v.replace(' 주', ''), 10) || 1}
                />
              </Col>
              <Col>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => setQuantity((q) => q + 1)}
                />
              </Col>
            </Row>
          </div>

          {/* 가격 조절 */}
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              주문 가격
            </Text>
            <Row align="middle" gutter={8}>
              <Col>
                <Button
                  icon={<MinusOutlined />}
                  onClick={() => setPrice((p) => Math.max(100, p - 100))}
                  disabled={price <= 100}
                />
              </Col>
              <Col flex="auto">
                <InputNumber
                  value={price}
                  onChange={(val) => setPrice(val || 0)}
                  min={0}
                  step={100}
                  style={{ width: '100%' }}
                  formatter={(v) =>
                    v ? `${Number(v).toLocaleString()} 원` : '0 원'
                  }
                  parser={(v) =>
                    parseInt(v.replace(/[^\d]/g, ''), 10) || 0
                  }
                />
              </Col>
              <Col>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => setPrice((p) => p + 100)}
                />
              </Col>
            </Row>
          </div>

          {/* 총 결제 금액 */}
          <div
            style={{
              background: '#fff1f0',
              borderRadius: 6,
              padding: '10px 14px',
              marginBottom: 12,
            }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Text strong>총 결제 금액</Text>
              </Col>
              <Col>
                <Text strong style={{ fontSize: 20, color: '#f5222d' }}>
                  {totalAmount.toLocaleString()}원
                </Text>
              </Col>
            </Row>
          </div>

          {/* 계좌 선택 */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              주문 계좌
            </Text>
            {accounts.length > 0 ? (
              <Select
                value={selectedAccount}
                onChange={setSelectedAccount}
                style={{ width: '100%' }}
                placeholder="계좌 선택"
              >
                {accounts.map((acc) => (
                  <Option key={acc.account_number} value={acc.account_number}>
                    {acc.account_name} ({acc.account_number})
                  </Option>
                ))}
              </Select>
            ) : (
              <Alert
                message="등록된 키움 계좌가 없습니다."
                type="warning"
                showIcon
              />
            )}
          </div>
        </Card>

        {/* 가격 추종 설정 카드 */}
        <Card
          style={{ marginBottom: 16, borderRadius: 8 }}
          bodyStyle={{ padding: '16px' }}
        >
          <Row justify="space-between" align="middle" style={{ marginBottom: priceFollow ? 12 : 0 }}>
            <Col>
              <Text strong>가격 추종</Text>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  현재가 변동에 따라 주문가 자동 조정
                </Text>
              </div>
            </Col>
            <Col>
              <Switch
                checked={priceFollow}
                onChange={setPriceFollow}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </Col>
          </Row>

          {priceFollow && (
            <>
              <div style={{ marginTop: 8 }}>
                <Row justify="space-between">
                  <Col>
                    <Text>최대 추종 범위</Text>
                  </Col>
                  <Col>
                    <Text strong style={{ color: '#1890ff' }}>
                      {maxFollowRange}%
                    </Text>
                  </Col>
                </Row>
                <Slider
                  min={0.5}
                  max={10}
                  step={0.5}
                  value={maxFollowRange}
                  onChange={setMaxFollowRange}
                  marks={{ 1: '1%', 3: '3%', 5: '5%', 10: '10%' }}
                />
              </div>
              <Alert
                message={`현재가 기준 ±${maxFollowRange}% 범위 내에서 주문가를 자동 조정합니다.`}
                type="info"
                style={{ marginTop: 8 }}
              />
            </>
          )}
        </Card>

        {/* 하단 버튼 */}
        <Row gutter={12}>
          <Col span={10}>
            <Button
              block
              size="large"
              onClick={handleCancel}
              style={{
                background: '#b37feb',
                borderColor: '#b37feb',
                color: '#fff',
                borderRadius: 8,
              }}
            >
              취소
            </Button>
          </Col>
          <Col span={14}>
            <Button
              block
              size="large"
              type="primary"
              loading={submitting}
              disabled={!selectedAccount || price <= 0 || quantity <= 0}
              onClick={handleConfirm}
              style={{
                background: orderType === 'buy' ? '#f5222d' : '#1890ff',
                borderColor: orderType === 'buy' ? '#f5222d' : '#1890ff',
                borderRadius: 8,
                fontWeight: 'bold',
              }}
            >
              {orderType === 'buy' ? '매수 실행' : '매도 실행'}
            </Button>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default QuickTrade;
